import axiosLib from 'axios'
import { recordSessionEnd, sessionEndFromRefreshError } from './lib/sessionEnd'

function getHeaders() {
  const headers = {}
  headers.Accept = 'application/json'
  const token = localStorage.getItem('authentication')
  headers.Authorization = `Bearer ${token}`
  return headers
}

let url = import.meta.env.VITE_API_URL
if (url === '') {
  url = window.location.protocol + '//api.' + window.location.host
}

export const axios = axiosLib.create({
  baseURL: url,
  timeout: 10000,
  headers: getHeaders(),
})

let requestTimestamp = null

// A single transient failure (a mobile radio blip, a backgrounded tab) should
// not raise the "lost contact" overlay. We only surface 'unreacheable' after
// this many consecutive network failures; any successful response resets it.
export const NETWORK_FAILURE_THRESHOLD = 2
let networkFailures = 0

function dispatchServerStatus(status) {
  if (typeof window.vue !== 'undefined') {
    window.vue.$store.dispatch('serverStatus', status)
  }
}

axios.interceptors.request.use(
  async (config) => {
    requestTimestamp = Date.now()
    /*
  if(typeof window.vue.$store.state.auth === 'undefined') {
    return Promise.resolve(config)
  }
  const c = await window.vue.$store.dispatch('auth/refreshIfNeeded').then(() => {
    requestTimestamp = Date.now()
    config.headers['Authorization'] = `Bearer ${localStorage.getItem('authentication')}`
    return Promise.resolve(config)
  })
  */
    config.headers['Calling-URL'] = window.location.href
    return config
  },
  (error) => Promise.reject(error)
)

export function onResponseSuccess(res) {
  // Any successful response means the server is reachable again, so clear the
  // transient-failure counter that gates the 'unreacheable' overlay.
  networkFailures = 0
  const responseTime = Date.now() - requestTimestamp
  if (typeof window.vue !== 'undefined') {
    window.vue.$store.dispatch('setResponseTime', responseTime)
    const serverVersion = res.headers['x-app-version']
    if (serverVersion && serverVersion !== __APP_VERSION__) {
      window.vue.$store.dispatch('setVersionMismatch', true)
    }
  }
  return res
}

export const REFRESH_URL = '/auth/refresh'

// #355: ONE renewal in flight per tab. Every 401 awaits this same promise — the
// first caller creates it, the rest join it.
//
// Before this, onResponseError fired a fresh POST /auth/refresh per 401. An
// admin tab loads ~6 requests in parallel (member_applications,
// expense_submissions, invoice_requests, km_maps, settings, websocket/auth), so
// a session that stopped being valid spent ~6 renewals instead of 1. Each
// rejected one answers 403, which the per-IP auth tier (#327) billed as a failed
// attempt: two tabs on one IP emptied a burst-10 bucket and the next POST /auth
// — with the correct password — was refused with a 429 for ~60s. It also had N
// concurrent renewals racing to write the same two localStorage keys.
//
// Cleared on success, so the next access-token expiry can renew again. Kept on
// failure on purpose: a later 401 in a tab that is already redirecting to the
// login screen reuses the rejection instead of spending another token.
let refreshInFlight = null

// applyRefreshedSession installs a renewed token pair. Runs exactly once per
// renewal — inside the shared promise, not per waiting request — so the
// localStorage writes and the store dispatches have a single writer.
function applyRefreshedSession(data) {
  localStorage.setItem('authentication', data.access)
  localStorage.setItem('refreshToken', data.refresh)
  axios.defaults.headers.common['Authorization'] = `Bearer ${data.access}`
  setHeader('Authorization', `Bearer ${data.access}`)
  if (typeof window.vue !== 'undefined' && typeof window.vue.$store.state.auth !== 'undefined') {
    window.vue.$store.dispatch('auth/setRefreshing', false)
    window.vue.$store.dispatch('auth/setLoginData', data)
    // #379: fire-and-forget, so its rejection has nobody to reach. A connection
    // lost in the instant after a renewal — the ordinary path of someone landing
    // on `/` with an old session and being sent to the login screen — made this
    // GET reject into the void, and Sentry recorded it as an unhandled
    // `AxiosError: Network Error`. The action itself keeps rejecting on purpose:
    // App.vue gates `appReady` on it at boot. The catch belongs at the floating
    // call, here.
    window.vue.$store.dispatch('explainer/load').catch((err) => console.error(err))
  }
}

export function refreshSession() {
  if (refreshInFlight === null) {
    const refreshToken = localStorage.getItem('refreshToken')
    refreshInFlight = axios.post(REFRESH_URL, { refreshToken: refreshToken }).then((res) => {
      const access = res?.data?.access
      if (typeof access === 'undefined') {
        // A 2xx carrying no token is not a usable session. Throwing routes it
        // through the same logged-out path as a 403, so every waiter shares one
        // outcome (the old code redirected here without clearing localStorage,
        // leaving a dead token behind to 401 again on the next page).
        throw new Error('refresh response carried no access token')
      }
      applyRefreshedSession(res.data)
      refreshInFlight = null
      return access
    })
  }
  return refreshInFlight
}

// endSession is the logged-out path: record WHY before wiping storage, then
// redirect. The login page consumes the record and says what happened (#355).
function endSession(refreshError) {
  recordSessionEnd(sessionEndFromRefreshError(refreshError))
  localStorage.clear()
  window.location.href = '/'
}

// #421 — the logging floor.
//
// A 5xx leaves no trace anywhere today. The API does not die of one: sentryRecovery
// (api/main.go:510, wired at :167) recovers the panic, reports it and writes a
// BODYLESS 500, and the process carries on — so the websocket never drops and no
// overlay is raised. Nothing below matches it either: it is not a network code,
// not the 503 boot gate, so it falls to the last `return Promise.reject`. And on
// the store side `err.response.data` is '' for a bodyless 500, so
// `err.response.data.errors` is undefined and generateStore's `setErrors` is a
// no-op that does not even clear the previous errors. Put one of the mute
// `.catch`es in the way and the button does nothing, silently.
//
// Measured on a dev stack whose GET /members panicked: the cooperador list
// rendered blank, the footer still said «tempo da última resposta: 100 ms», and
// the console held two lines, both Vite's own.
//
// This LOGS. It shows nothing: an interceptor cannot know whether the component
// already drew the error, so a banner here would double up on the 422s the forms
// already put on the fields. That half is #422.
//
// The exclusions are the design, not a detail of it:
//
//   401  the renewal path above, which logs its own failure and is routine every
//        time an access token expires;
//   503  the boot readiness gate (#270) — it has its own state and recovers by
//        itself. It is the only exclusion inside the 5xx band, and the only one
//        this function has to test for;
//   404  how the API says "no rows" (API.CheckEmpty). Not hypothetical: a
//        healthy admin page load already answers 404 on
//        /expense_submissions?_attention=true, so logging it would put an error
//        in the console of every organisation with no data, on every visit;
//   422  validation refusals, already drawn on the fields.
//
// The last three sit below the 5xx band, so the band itself excludes them; they
// are named here and pinned in request.test.js because a later widening of this
// floor has to come past them deliberately. The rest of the 4xx stays out for
// the same reason — a 409 is a duplicate somebody typed, a 429 is the auth
// throttle (#327/#343) working as designed, a 413 is an upload the form already
// refuses. None of those is a missing trace.
//
// Cancelled requests are excluded by having no response at all: generateStore
// aborts the in-flight list on every supersede (beginListRequest), so typing
// into a search box cancels one request per keystroke.
//
// A 404 that is really a failed query is invisible to this rule, on purpose:
// today the API cannot tell the two apart (#424). Once #424 makes a genuine
// query failure answer 500, this floor catches it with no change here.
//
// Note this reaches the console, not Sentry: @sentry/vue turns a console call
// into a breadcrumb on some later event, never an event of its own, and
// captureConsoleIntegration is deliberately not configured (37 console.error
// calls in src/ were not written with Sentry in mind). Raising a Sentry event
// for these is tracked separately.
function logUnexpectedResponse(error) {
  const status = error.response?.status
  if (typeof status !== 'number' || status < 500 || status === 503) {
    return
  }
  const method = (error.config?.method ?? 'get').toUpperCase()
  const url = error.config?.url ?? '(unknown url)'
  // One string, not several arguments and no object: App.vue wraps console.error
  // in a toast under import.meta.env.DEV and joins `arguments`, so an object
  // would reach the screen as "[object Object]".
  console.error(`API request failed: ${status} on ${method} ${url}`)
}

export async function onResponseError(error) {
  const originalRequest = error.config
  if (typeof error.response !== 'undefined') {
    if (
      error.response.status === 401 &&
      !originalRequest._retry &&
      // The renewal request itself must never enter this branch: it would await
      // the very promise it is part of and hang the request for good.
      originalRequest.url !== REFRESH_URL &&
      typeof window.vue.$store.state.auth !== 'undefined'
    ) {
      window.vue.$store.dispatch('auth/setRefreshing', true)
      originalRequest._retry = true // Mark the request as retried to avoid infinite loops.
      try {
        const access = await refreshSession()
        if (originalRequest.headers) {
          originalRequest.headers['Authorization'] = `Bearer ${access}`
        }
        return axios(originalRequest) // Retry the original request with the new access token.
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError)
        endSession(refreshError)
        return new Promise(() => {}) // Never resolves — page is redirecting
      }
    }
  }
  // The backend readiness gate (#270) answers 503 ("service starting") while
  // it boots. Surface it as its own state so the overlay shows "server
  // starting" and auto-recovers, instead of leaking an unhandled rejection.
  if (error.response && error.response.status === 503) {
    dispatchServerStatus('starting')
    return Promise.reject(error)
  }

  // Real connectivity failures. Debounce so a single transient blip does not
  // raise the overlay (#260); reject rather than hang so the caller's own
  // .catch can run (e.g. mark an upload row errored instead of freezing).
  if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
    networkFailures += 1
    if (networkFailures >= NETWORK_FAILURE_THRESHOLD) {
      dispatchServerStatus('unreacheable')
    }
    return Promise.reject(error)
  }

  // Everything that reaches here had no channel of its own. Leave a trace before
  // handing the rejection on untouched, so every existing .catch behaves exactly
  // as it did.
  logUnexpectedResponse(error)
  return Promise.reject(error)
}

axios.interceptors.response.use(onResponseSuccess, onResponseError)

export function setHeader(key, value) {
  axios.defaults.headers[key] = value
}
